import { onScopeDispose, readonly, ref, watch, type Ref } from 'vue'

// 'dismissed': the prompt closed without an answer, so asking again re-prompts.
// 'blocked': the site permission is denied; only the browser's site settings undo it.
export type MicState = 'unknown' | 'prompt' | 'requesting' | 'granted' | 'dismissed' | 'blocked' | 'unavailable'
export type MicProblem = 'no_device' | 'busy' | 'system_blocked' | 'unsupported'

// Reading an allowed mic opens no prompt, so one this slow has hung (a wedged or virtual device).
export const MIC_READ_TIMEOUT_MS = 8000
const DEVICE_CHANGE_SETTLE_MS = 300
const DEVICE_CHANGE_QUIET_MS = 2000

// Module-level singleton: the permission belongs to the browser, not to a page.
const state = ref<MicState>('unknown')
const problem = ref<MicProblem | null>(null)
// The site permission as the browser reports it; null when it can't say.
const permission = ref<PermissionState | null>(null)
// Counts the browser's permission changes, so two that land in one tick (off, then on) still register.
const permissionChanges = ref(0)
// The mic the browser handed out on the last successful read, cleared whenever the mic isn't usable.
// Calls ask the same way (no input device is ever set), so it is the mic they use.
const device = ref<string | null>(null)
let status: PermissionStatus | null = null
let initPromise: Promise<void> | null = null
let pending: Promise<void> | null = null
// The ask in flight. A permission change lets a silent one go, so it can't overwrite the change.
let asking: { token: number; silent: boolean } | null = null
let askSeq = 0
// When the last ask began: opening a mic can make Bluetooth headsets swap profiles, which the
// device-change listener must not mistake for the rep plugging something in.
let lastAskAt = -Infinity

type Outcome = { state: MicState; problem: MicProblem | null; device: string | null }

function set(next: MicState, why: MicProblem | null = null): void {
  state.value = next
  problem.value = why
  if (next !== 'granted') device.value = null
}

function fromPermission(p: PermissionState | null): MicState {
  if (p === 'granted') return 'granted'
  if (p === 'denied') return 'blocked'
  return 'prompt'
}

function onPermissionChange(): void {
  if (!status) return
  permissionChanges.value++
  permission.value = status.state
  // A prompted ask classifies its own outcome.
  if (state.value === 'requesting') return
  // A silent read was asking under the old permission: let it go, so whatever reacts to the change
  // asks afresh.
  if (asking?.silent) {
    asking = null
    pending = null
  }
  set(fromPermission(status.state))
}

// null when the browser can't say (Firefox before 131 rejects the 'microphone' name).
async function readPermission(): Promise<PermissionState | null> {
  try {
    if (!status) {
      status = await navigator.permissions.query({ name: 'microphone' as PermissionName })
      status.addEventListener('change', onPermissionChange)
    }
    permission.value = status.state
    return status.state
  } catch {
    return null
  }
}

function init(): Promise<void> {
  initPromise ??= (async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      set('unavailable', 'unsupported')
      return
    }
    const p = await readPermission()
    if (state.value === 'unknown') set(fromPermission(p))
  })()
  return initPromise
}

function stopTracks(stream: MediaStream): void {
  for (const track of stream.getTracks()) track.stop()
}

// null once `ms` pass; a stream that turns up later is released straight away.
function withTimeout(ask: Promise<MediaStream>, ms: number): Promise<MediaStream | null> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      resolve(null)
      ask.then(stopTracks, () => {})
    }, ms)
    ask.then(
      (stream) => {
        clearTimeout(timer)
        resolve(stream)
      },
      (err) => {
        clearTimeout(timer)
        reject(err)
      },
    )
  })
}

async function classify(err: unknown): Promise<Outcome> {
  console.warn('[useMicPermission] getUserMedia failed', err)
  const { name, message } = (err ?? {}) as { name?: string; message?: string }
  const outcome = (s: MicState, why: MicProblem | null = null): Outcome => ({ state: s, problem: why, device: null })
  if (name === 'NotAllowedError' || name === 'SecurityError') {
    // The same error covers a Block, a closed prompt and an OS-level denial.
    const p = await readPermission()
    if (p === 'denied') return outcome('blocked')
    if (p === 'granted' || /system/i.test(String(message))) return outcome('unavailable', 'system_blocked')
    return outcome('dismissed')
  }
  if (name === 'NotFoundError' || name === 'OverconstrainedError') return outcome('unavailable', 'no_device')
  return outcome('unavailable', 'busy')
}

async function attempt(silent: boolean): Promise<Outcome> {
  let stream: MediaStream | null
  try {
    const asked = navigator.mediaDevices.getUserMedia({ audio: true })
    stream = silent ? await withTimeout(asked, MIC_READ_TIMEOUT_MS) : await asked
  } catch (err) {
    return classify(err)
  }
  if (!stream) return { state: 'unavailable', problem: 'busy', device: null }
  const label = stream.getAudioTracks()[0]?.label ?? ''
  // Only the grant and the name are wanted; a held stream keeps the mic and its indicator live.
  stopTracks(stream)
  return { state: 'granted', problem: null, device: label }
}

async function ask(): Promise<void> {
  const token = ++askSeq
  lastAskAt = performance.now()
  // An allowed site opens no prompt: the named mic stays up while it is read again, and a read that
  // hangs gives up rather than leave the rep on "Checking…".
  const silent = permission.value === 'granted'
  asking = { token, silent }
  if (!silent) set('requesting')
  let outcome: Outcome
  try {
    outcome = await attempt(silent)
  } catch (err) {
    console.warn('[useMicPermission] reading the mic failed', err)
    outcome = { state: 'unavailable', problem: 'busy', device: null }
  }
  // Let go by a permission change, which has the last word.
  if (asking?.token !== token) return
  asking = null
  // Cleared before the state moves, so whatever reacts to it can ask afresh.
  pending = null
  set(outcome.state, outcome.problem)
  if (outcome.device !== null) device.value = outcome.device
}

// May open the browser's prompt, so only a rep's click calls it.
function request(): Promise<void> {
  // Nothing to call, and ask() would read the TypeError as a busy mic.
  if (!navigator.mediaDevices?.getUserMedia) {
    set('unavailable', 'unsupported')
    return Promise.resolve()
  }
  pending ??= ask()
  return pending
}

// Never opens a prompt: only a site the browser already allows can be read silently.
function read(): Promise<void> {
  return permission.value === 'granted' ? request() : Promise.resolve()
}

// The mic as a rep knows it; Chrome and Windows prefix the system default's role.
export function micName(label: string | null): string {
  return (label ?? '').replace(/^(Default|Communications) - /, '') || 'Your default microphone'
}

async function audioInputs(): Promise<string> {
  const all = (await navigator.mediaDevices?.enumerateDevices?.()) ?? []
  return all
    .filter((d) => d.kind === 'audioinput')
    .map((d) => d.deviceId)
    .sort()
    .join(',')
}

/**
 * While `active`, keeps the named mic current: read once the browser allows it, and again when the rep
 * comes back to the page or the mics plugged in change. With `ask`, the browser's prompt also opens
 * whenever it can be answered.
 */
export function useMicReading(active: Readonly<Ref<boolean>>, opts: { ask: boolean }): void {
  watch(
    [active, state, permissionChanges],
    ([on, s], old) => {
      if (!on) return
      void init()
      // Coming into view names the mic afresh: it may have changed while nobody was looking.
      const arrived = !old?.[0]
      if (opts.ask && s === 'prompt') void request()
      else if (s === 'granted' && (arrived || device.value === null)) void read()
    },
    { immediate: true },
  )

  // Moves whenever the view opens or closes, so nothing started for an old view lands in a new one.
  let generation = 0
  let inputs: string | null = null
  let settle: ReturnType<typeof setTimeout> | null = null
  function onFocus(): void {
    if (state.value === 'granted') void read()
  }
  function onDevices(): void {
    if (settle) clearTimeout(settle)
    const gen = generation
    settle = setTimeout(async () => {
      settle = null
      const next = await audioInputs()
      if (gen !== generation) return
      if (problem.value === 'no_device') {
        // Before a grant the ids are blank, so a new mic can't be told apart: any change is worth a try.
        inputs = next
        void (opts.ask ? request() : read())
        return
      }
      // Opening the mic can make a Bluetooth headset swap profiles: our own doing, not a new mic.
      if (next === inputs || performance.now() - lastAskAt < DEVICE_CHANGE_QUIET_MS) return
      inputs = next
      if (state.value === 'granted') void read()
    }, DEVICE_CHANGE_SETTLE_MS)
  }
  function listen(on: boolean): void {
    generation++
    if (settle) clearTimeout(settle)
    settle = null
    if (on) {
      window.addEventListener('focus', onFocus)
      navigator.mediaDevices?.addEventListener?.('devicechange', onDevices)
      const gen = generation
      void audioInputs().then((v) => {
        if (gen === generation) inputs ??= v
      })
    } else {
      window.removeEventListener('focus', onFocus)
      navigator.mediaDevices?.removeEventListener?.('devicechange', onDevices)
      inputs = null
    }
  }
  watch(active, listen, { immediate: true })
  onScopeDispose(() => listen(false))
}

export function useMicPermission() {
  return {
    state: readonly(state),
    problem: readonly(problem),
    permission: readonly(permission),
    device: readonly(device),
    init,
    request,
    read,
  }
}
